import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import useUserProfile, { Profile, profileDataPlaceholder } from 'src/services/profile';

interface ProfileContextValue {
  profile: Profile;
  picture: string | null;
  loading: boolean;
  error: any;
  updateProfileData: (data: Partial<Profile>, silent?: boolean) => Promise<void>;
  updateProfilePicture: (file: File) => Promise<void>;
  deleteProfilePicture: () => Promise<void>;
  updateUserPasswords: (current: string, next: string) => Promise<boolean>;
  updateDarkMode: (value: boolean) => Promise<void>;
  setProfile: (profile: Profile) => void;
}

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const {
    profile,
    picture,
    error,
    loading,
    updateProfileData,
    updateProfilePicture,
    deleteProfilePicture,
    updateUserPasswords,
    updateDarkMode,
  } = useUserProfile();

  const [profileState, setProfileState] = useState<Profile>(profileDataPlaceholder);

  useEffect(() => {
    if (profile) setProfileState(profile);
  }, [profile]);

  const value = useMemo(
    () => ({
      profile: profileState,
      picture,
      loading,
      error,
      updateProfileData: async (data: Partial<Profile>, silent = false) => {
        if (!profileState) return;
        const fullData: Profile = { ...profileState, ...data };
        await updateProfileData(fullData, silent);
        setProfileState(fullData);
      },
      updateProfilePicture: async (file: File) => {
        await updateProfilePicture(file);
      },
      deleteProfilePicture,
      updateUserPasswords,
      updateDarkMode: async (value: boolean) => {
        setProfileState(prev => ({ ...prev, is_in_dark_mode: value }));
        await updateDarkMode(value);
      },
      setProfile: setProfileState,
    }),
    [
      profileState,
      picture,
      loading,
      error,
      updateProfileData,
      updateProfilePicture,
      deleteProfilePicture,
      updateUserPasswords,
      updateDarkMode,
    ],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export const useProfile = (): ProfileContextValue => {
  const context = useContext(ProfileContext);
  if (!context) throw new Error('useProfile must be used within a ProfileProvider');
  return context;
};
